import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft, ArrowRight, Play, CheckCircle2, XCircle, Clock,
    Loader2, HandMetal,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/* ================================================================
   Nexus One · OrchestrationRuns — history list per-orchestration
   ================================================================ */

const STATUS_STYLES = {
    running:            { color: "#8b5cf6", bg: "rgba(139,92,246,0.14)", border: "rgba(139,92,246,0.55)" },
    awaiting_approval:  { color: "#fbbf24", bg: "rgba(245,158,11,0.14)", border: "rgba(245,158,11,0.55)" },
    success:            { color: "#34d399", bg: "rgba(52,211,153,0.14)", border: "rgba(52,211,153,0.55)" },
    failed:             { color: "#f87171", bg: "rgba(239,68,68,0.14)", border: "rgba(239,68,68,0.45)" },
    cancelled:          { color: "#94a3b8", bg: "rgba(148,163,184,0.14)", border: "rgba(148,163,184,0.45)" },
    pending:            { color: "#94a3b8", bg: "rgba(148,163,184,0.14)", border: "rgba(148,163,184,0.45)" },
};

export default function OrchestrationRuns() {
    const { orchId } = useParams();
    const nav = useNavigate();
    const [orch, setOrch] = useState(null);
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [o, r] = await Promise.all([
                api.get(`/orchestrations/${orchId}`),
                api.get(`/orchestrations/${orchId}/runs`),
            ]);
            setOrch(o.data);
            setRuns(r.data.runs || []);
        } catch (e) { toast.error("Could not load runs."); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [orchId]);

    const runNow = async () => {
        setRunning(true);
        try {
            const { data } = await api.post(`/orchestrations/${orchId}/run`, { trigger_payload: {} });
            nav(`/app/orchestrations/${orchId}/runs/${data.run_id}`);
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Run failed to start.");
        } finally { setRunning(false); }
    };

    return (
        <div className="h-full overflow-y-auto atlas-scrollbar" data-testid="orch-runs">
            <div className="max-w-5xl mx-auto px-8 py-10">
                <Link to="/app/orchestrations"
                    className="inline-flex items-center gap-1 text-[12px]"
                    style={{ color: "var(--atlas-text-tertiary)" }}
                    data-testid="orch-runs-back">
                    <ArrowLeft className="w-3 h-3" strokeWidth={2} /> Orchestrations
                </Link>
                <div className="mt-4 flex items-end justify-between gap-4">
                    <div>
                        <div className="atlas-hero-eyebrow">
                            <Clock className="w-3 h-3" strokeWidth={2.5} /> Run history
                        </div>
                        <h1 className="atlas-text-gradient mt-3 text-[30px] font-semibold tracking-tight">
                            {orch?.name || "Loading…"}
                        </h1>
                        <p className="mt-1 text-[13px]" style={{ color: "var(--atlas-text-tertiary)" }}>
                            {orch?.description}
                        </p>
                    </div>
                    {orch?.status === "active" && (
                        <button onClick={runNow} disabled={running}
                            className="atlas-btn-primary inline-flex items-center gap-1.5 rounded-lg px-4 h-11 text-[13.5px]"
                            data-testid="orch-runs-run-now">
                            {running ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2}/> : <HandMetal className="w-4 h-4" strokeWidth={2}/>}
                            Run now
                        </button>
                    )}
                </div>

                {loading ? (
                    <div className="mt-10 text-[13px]" style={{ color: "var(--atlas-text-muted)" }}>Loading…</div>
                ) : runs.length === 0 ? (
                    <div className="mt-8 atlas-card p-8 text-center" data-testid="orch-runs-empty">
                        <div className="text-[15px] font-semibold" style={{ color: "var(--atlas-text-primary)" }}>
                            No runs yet.
                        </div>
                        <p className="mt-1 text-[12.5px]" style={{ color: "var(--atlas-text-tertiary)" }}>
                            Activate this orchestration and hit “Run now” to kick off your first execution.
                        </p>
                    </div>
                ) : (
                    <div className="mt-8 space-y-2" data-testid="orch-runs-list">
                        {runs.map((r) => {
                            const st = STATUS_STYLES[r.status] || STATUS_STYLES.pending;
                            return (
                                <Link key={r.id} to={`/app/orchestrations/${orchId}/runs/${r.id}`}
                                    className="block atlas-card atlas-card--hoverable p-4"
                                    data-testid={`orch-run-row-${r.id}`}>
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="text-[13px] font-mono truncate"
                                                style={{ color: "var(--atlas-text-primary)" }}>
                                                {r.id.slice(0, 8)}…
                                            </div>
                                            <div className="mt-0.5 text-[11.5px]"
                                                style={{ color: "var(--atlas-text-tertiary)" }}>
                                                {(r.node_runs || []).length} node runs · started {r.started_at?.slice(0, 19).replace("T", " ")}
                                            </div>
                                        </div>
                                        <span className="text-[9.5px] font-bold tracking-widest px-2 py-0.5 rounded-full"
                                            style={{ background: st.bg, border: `1px solid ${st.border}`, color: st.color }}>
                                            {r.status?.toUpperCase().replace(/_/g, " ")}
                                        </span>
                                        <ArrowRight className="w-4 h-4" strokeWidth={2}
                                            style={{ color: "var(--atlas-text-muted)" }} />
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                )}
                <div className="h-16" />
            </div>
        </div>
    );
}
